import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Dashboard/Sidebar';
import { JobListingsSection } from '../components/Dashboard/JobListingsSection';
import { ProfileSummary } from '../components/Dashboard/ProfileSummary';
import { JobListing } from '../components/Dashboard/JobCard';
import { JobDetailsModal } from '../components/Dashboard/JobDetailsModal';
import { WelcomeModal } from '../components/Onboarding/WelcomeModal';
import { apiPost } from '../utils/apiUtils';

interface CacheInfo {
    lastFetchAt: string | null;
    daysUntilNextFetch: number;
    message: string;
}

export const CandidateDashboardPage: React.FC = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState<JobListing[]>([]);
    const [loadingJobs, setLoadingJobs] = useState(true);
    const [loadingUserName, setLoadingUserName] = useState(true);
    const [selectedJob, setSelectedJob] = useState<JobListing | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCached, setIsCached] = useState(false);
    const [cacheInfo, setCacheInfo] = useState<CacheInfo | null>(null);
    const [userName, setUserName] = useState<string>('');
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const fetchPromiseRef = React.useRef<Promise<void> | null>(null);

    // Redirect to home if not authenticated
    useEffect(() => {
        if (!loading && !user) {
            navigate('/');
        }
    }, [user, loading, navigate]);

    // Check if we should show welcome modal (once per login session)
    useEffect(() => {
        if (!user) return;
        
        // Check if modal has been shown this session
        const shownThisSession = sessionStorage.getItem('welcomeModalShownThisSession');
        
        // If not shown this session, show it
        if (!shownThisSession) {
            setShowWelcomeModal(true);
            // Mark as shown for this session
            sessionStorage.setItem('welcomeModalShownThisSession', 'true');
        }
        
        // Also handle the case where user just completed onboarding
        const fromOnboarding = sessionStorage.getItem('showWelcomeModal');
        if (fromOnboarding === 'true') {
            sessionStorage.removeItem('showWelcomeModal');
            // Already showing modal above, no need to set again
        }
    }, [user]);

    // Fetch user's name from database
    useEffect(() => {
        if (!user?.id) return;

        const fetchUserName = async () => {
            try {
                setLoadingUserName(true);
                const data = await apiPost('/api/get-user-profile', {
                    userData: {
                        sub: user.id,
                        email: user.email,
                    },
                });

                if (data.success && data.profile && data.profile.name) {
                    setUserName(data.profile.name);
                } else {
                    setUserName(user.user_metadata?.full_name || 'User');
                }
            } catch (error) {
                console.error('[Dashboard] Error fetching user name:', error);
                setUserName(user.user_metadata?.full_name || 'User');
            } finally {
                setLoadingUserName(false);
            }
        };

        fetchUserName();
    }, [user?.id, user?.email, user?.user_metadata?.full_name]);

    // Fetch jobs based on user preferences
    useEffect(() => {
        if (!user?.id) return;

        if (fetchPromiseRef.current) {
            return;
        }

        const fetchJobs = async () => {
            try {
                setLoadingJobs(true);
                const data = await apiPost('/api/jobs/theirstack-match', {
                    userData: {
                        email: user.email,
                        sub: user.id,
                        name: user.user_metadata?.full_name,
                        picture: user.user_metadata?.avatar_url,
                    },
                    page: 0,
                    limit: 3,
                });

                if (data.success && data.jobs) {
                    setJobs(data.jobs);
                    setIsCached(data.cached || false);

                    if (data.cached && data.cacheInfo) {
                        // Ensure daysUntilNextFetch is a valid number
                        const cacheInfo = {
                            ...data.cacheInfo,
                            daysUntilNextFetch: typeof data.cacheInfo.daysUntilNextFetch === 'number' 
                                ? data.cacheInfo.daysUntilNextFetch 
                                : parseFloat(data.cacheInfo.daysUntilNextFetch) || 0
                        };
                        console.log('[Dashboard] Cache info received:', {
                            cached: data.cached,
                            cacheInfo: data.cacheInfo,
                            processedCacheInfo: cacheInfo,
                            daysUntilNextFetch: cacheInfo.daysUntilNextFetch
                        });
                        setCacheInfo(cacheInfo);
                    } else {
                        setCacheInfo(null);
                    }
                }
            } catch (error: any) {
                console.error('[Dashboard] Error fetching jobs:', error);
                setJobs([]);
            } finally {
                setLoadingJobs(false);
                fetchPromiseRef.current = null;
            }
        };

        fetchPromiseRef.current = fetchJobs();

    }, [user?.id, user?.email, user?.user_metadata?.full_name, user?.user_metadata?.avatar_url]);

    const handleJobClick = (job: JobListing) => {
        setSelectedJob(job);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedJob(null);
    };

    const handleWelcomeModalClose = () => {
        setShowWelcomeModal(false);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mb-4"></div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-2">Loading...</h2>
                </div>
            </div>
        );
    }

    if (!user) {
        return null;
    }

    return (
        <div className="dashboard-layout">
            <Sidebar />

            <div className="main-content">
                <div className="max-w-7xl mx-auto">
                    {/* Header Section */}
                    <div className="mb-6 md:mb-10 animate-fade-in">
                        <h1 className="heading-1 mb-2">
                            Welcome back {loadingUserName ? (
                                <span className="inline-block h-7 md:h-9 w-32 md:w-40 bg-slate-200 rounded-lg animate-pulse align-middle"></span>
                            ) : (
                                <span className="text-gradient-blue">{userName}</span>
                            )}
                        </h1>
                    </div>

                    {/* Profile Summary */}
                    <div className="mb-6 md:mb-10 animate-fade-in animate-delay-100">
                        <ProfileSummary />
                    </div>

                    {/* Job Listings */}
                    <div className="animate-fade-in animate-delay-300">
                        <div className="flex items-center justify-between mb-6">
                        </div>

                        {loadingJobs ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="card-premium h-56 md:h-64 p-4 md:p-6 animate-pulse">
                                        <div className="h-10 w-10 md:h-12 md:w-12 bg-slate-200 rounded-xl mb-3 md:mb-4"></div>
                                        <div className="h-5 md:h-6 bg-slate-200 rounded w-3/4 mb-2 md:mb-3"></div>
                                        <div className="h-3 md:h-4 bg-slate-200 rounded w-1/2 mb-4 md:mb-6"></div>
                                        <div className="space-y-2">
                                            <div className="h-2.5 md:h-3 bg-slate-200 rounded w-full"></div>
                                            <div className="h-2.5 md:h-3 bg-slate-200 rounded w-5/6"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <JobListingsSection 
                                jobs={jobs} 
                                onJobClick={handleJobClick}
                                cacheInfo={cacheInfo}
                                isCached={isCached}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Job Details Modal */}
            {selectedJob && (
                <JobDetailsModal
                    job={selectedJob}
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                />
            )}

            {/* Welcome Modal */}
            <WelcomeModal
                isOpen={showWelcomeModal}
                onClose={handleWelcomeModalClose}
            />
        </div>
    );
};
